import React, { ChangeEvent, Dispatch, SetStateAction, useState } from 'react';
import { Button, Group, Dialog, Checkbox } from '@economic/taco';
import * as Yup from 'yup';

import track from '../../../../../../../shared/util/utilities/mixpanel';
import { t, tBB } from '../../../../../../../shared/components/l10n/l10n';
import { Form, Field } from '../../../../../../../shared/components/Form';
import ExternalLink from '../../../../../../../shared/components/ExternalLink';

interface Props {
    next: () => void;
    back: () => void;
    userDataStates: {
        firstNameState: [string, Dispatch<SetStateAction<string>>];
        lastNameState: [string, Dispatch<SetStateAction<string>>];
        emailState: [string, Dispatch<SetStateAction<string>>];
        phoneState: [string, Dispatch<SetStateAction<string>>];
        newsletterConsentState: [boolean, Dispatch<SetStateAction<boolean>>];
    };
}

const UserDataStep = ({
    next,
    back,
    userDataStates: { firstNameState, lastNameState, emailState, phoneState, newsletterConsentState },
}: Props) => {
    const [isNextButtonDisabled, setIsNextButtonDisabled] = useState(
        !firstNameState[0] || !lastNameState[0] || !emailState[0] || !phoneState[0]
    );

    const handleSubmit = () => {
        track('smartpay', 'signup.modal.contact-information-confirmed');
        if (typeof next === 'function') next();
    };

    const onFormChange = ({ values: { firstName, lastName, email, phone } }) => {
        setIsNextButtonDisabled(!firstName || !lastName || !email || !phone);
    };

    const setInputValue = (setter: Dispatch<SetStateAction<any>>) => (event: ChangeEvent<HTMLInputElement> & { detail: any }) => {
        setter(event.target.value);
    };

    const initialValues = {
        firstName: firstNameState[0],
        lastName: lastNameState[0],
        email: emailState[0],
        phone: phoneState[0],
        newsletterConsent: newsletterConsentState[0],
    };

    const userValidationSchema = Yup.object({
        firstName: Yup.string().required(),
        lastName: Yup.string().required(),
        email: Yup.string().required().email(),
        phone: Yup.string()
            .required()
            .matches(/^[+]{0,1}[\s0-9]*$/, t`SMARTPAY_ONBOARDING_MODAL_COMPANY_DATA_PHONE_VALIDATION_ERROR`),
        newsletterConsent: Yup.bool(),
    });

    return (
        <>
            <Dialog.Title>
                <div className="my-4">{t`SMARTPAY_ONBOARDING_MODAL_USER_DATA_TITLE`}</div>
            </Dialog.Title>

            <Form
                onChange={onFormChange}
                onSubmit={handleSubmit}
                skipNotifications
                initialData={initialValues}
                validationSchema={userValidationSchema}>
                <div className="flex flex-col">
                    <span>
                        {tBB('SMARTPAY_ONBOARDING_MODAL_USER_DATA_TEXT', {
                            __LINK: content => (
                                <ExternalLink href="https://www.e-conomic.dk/sikkerhed/privacy">{content}</ExternalLink>
                            ),
                        })}
                    </span>

                    <div className="my-2">
                        <Field.Text
                            label={t`SMARTPAY_ONBOARDING_MODAL_COMPANY_DATA_FIRST_NAME`}
                            name="firstName"
                            onBlur={setInputValue(firstNameState[1])}
                        />
                    </div>

                    <div className="my-2">
                        <Field.Text
                            label={t`SMARTPAY_ONBOARDING_MODAL_COMPANY_DATA_LAST_NAME`}
                            name="lastName"
                            onBlur={setInputValue(lastNameState[1])}
                        />
                    </div>

                    <div className="my-2">
                        <Field.Text
                            label={t`SMARTPAY_ONBOARDING_MODAL_COMPANY_DATA_PHONE`}
                            name="phone"
                            onBlur={setInputValue(phoneState[1])}
                        />
                    </div>

                    <div className="my-2">
                        <Field.Text
                            label={t`SMARTPAY_ONBOARDING_MODAL_COMPANY_DATA_EMAIL`}
                            name="email"
                            onBlur={setInputValue(emailState[1])}
                        />
                    </div>

                    <div className="my-2">
                        <Checkbox
                            label={
                                <span>
                                    {tBB('SMARTPAY_ONBOARDING_MODAL_NEWSLETTER_CONSENT', {
                                        __LINK: content => (
                                            <ExternalLink href="https://www.e-conomic.dk/lpd/nyhedsmails-app-partners">
                                                {content}
                                            </ExternalLink>
                                        ),
                                    })}
                                </span>
                            }
                            name="newsletterConsent"
                            checked={newsletterConsentState[0]}
                            onChange={value => newsletterConsentState[1](value)}
                        />
                    </div>
                </div>

                <Dialog.Footer>
                    <Group>
                        <Button onClick={back}>{t`SMARTPAY_ONBOARDING_MODAL_BACK_BUTTON`}</Button>
                        <Button appearance="primary" type="submit" disabled={isNextButtonDisabled}>
                            {t`SMARTPAY_ONBOARDING_MODAL_NEXT_BUTTON`}
                        </Button>
                    </Group>
                </Dialog.Footer>
            </Form>
        </>
    );
};

export default UserDataStep;
